import React from 'react'
// libs
import { useSnackbar } from 'notistack'
// hooks
import { useApolloClient } from 'hooks/useApolloClient'
// types
import { NGenerateUploadUrl } from 'types/upload'
// graphql
import { GENERATE_UPLOAD_URL_MUTATION, GENERATE_UPLOAD_URL_PUBLIC_MUTATION } from 'graphql/assets/mutations'
// context
import { PublicContext } from 'containers/Risks/Risk/Edit/public'

const waitUntilAvailable = async (url: string, maxAttempts = 20, intervalMs = 1500): Promise<void> => {
  for (let i = 0; i < maxAttempts; i++) {
    try {
      const res = await fetch(url, { method: 'HEAD', redirect: 'manual' })
      // 2xx = proxy returned directly, opaqueredirect = file passed scan (redirect to S3, browser fetch with redirect:'manual' returns status 0)
      if (res.ok || res.type === 'opaqueredirect') return
      // 425 Too Early = file is still being scanned, keep polling
    } catch {
      // network error — keep trying
    }

    if (i < maxAttempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs))
    }
  }
  throw new Error('File is not available after upload (firewall check timeout)')
}

export const useFileUpload = () => {
  const isPublic = React.useContext(PublicContext)

  const { client } = useApolloClient()

  const { enqueueSnackbar } = useSnackbar()

  const [uploading, setUploading] = React.useState(false)

  const upload = async (file: File | Blob, variables: NGenerateUploadUrl.IVariables): Promise<string> => {
    setUploading(true)
    try {
      if (!variables?.file?.contentType) {
        throw new Error('Missing contentType')
      }

      const {
        data: {
          [isPublic ? 'generateUploadUrlsPublic' : 'generateUploadUrls']: { downloadUrl, uploadUrl, uploadFormData }
        }
      } = await client.mutate<NGenerateUploadUrl.IResponse>({
        mutation: isPublic ? GENERATE_UPLOAD_URL_PUBLIC_MUTATION : GENERATE_UPLOAD_URL_MUTATION,
        variables
      })

      const form = new FormData()

      Object.entries(uploadFormData).forEach(([key, value]) => {
        form.append(key, value as string)
      })

      form.append('file', file)

      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: form
      })

      if (!response.ok) {
        const error = (await response.text()) || 'Upload failed'

        throw new Error(error)
      }

      await waitUntilAvailable(downloadUrl)

      return downloadUrl
    } catch (error) {
      console.log('[useFileUpload.upload]', error)
      enqueueSnackbar(error instanceof Error ? error.message : 'Upload failed', { variant: 'error' })

      throw error
    } finally {
      setUploading(false)
    }
  }

  return { upload, uploading }
}
